import React, { useEffect, useRef, useState } from 'react';
import walletService from './payService';
import './css/WalletContent.css';
import authService from '../auth/authService';
import { getApiErrorMessage } from '../auth/apiError';
import labService from '../lab/labService';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faFileInvoiceDollar, faEnvelope } from '@fortawesome/free-solid-svg-icons';
import HashLoader from "react-spinners/HashLoader";

// The payment endpoints can answer with raw Stripe text, so the wallet shows only its own sentences.
const BALANCE_ERROR_MESSAGE = 'Balance unavailable – please refresh.';
const HISTORY_ERROR_MESSAGE = 'Your payment history could not be loaded. Please try again later.';
const PURCHASE_ERROR_MESSAGE = "We couldn't start the payment. Please try again in a few minutes. If it keeps failing, contact us.";

// Logs only the status or code: a request error's config carries the access token.
const errorSummary = (error) => error?.response?.status ?? error?.code;

const WalletContent = () => {
    const [currentUser, setCurrentUser] = useState(null);
    const [user, setUser] = useState(null);
    const [loading, setLoading] = useState(true);
    const [sortConfig, setSortConfig] = useState({ key: 'created', direction: 'descending' });
    const [payments, setPayments] = useState([]);
    const [histLoading, setHistLoading] = useState(true);
    const [histError, setHistError] = useState(false);
    const [selectedAmount, setSelectedAmount] = useState(10);
    const [purchasing, setPurchasing] = useState(false);
    const [purchaseError, setPurchaseError] = useState('');
    const purchasingRef = useRef(false);


    useEffect(() => {
        const fetchUserData = async () => {
            const userData = localStorage.getItem('user');
            if (!userData) {
                setLoading(false);
                return;
            }

            try {
                const response = await labService.getUserInfo();
                setUser(response.data);
            } catch (error) {
                console.error('Error fetching user data', errorSummary(error));
            } finally {
                setLoading(false);
            }
        };

        fetchUserData();
    }, []);

    useEffect(() => {
        const user = authService.getCurrentUser();
        setCurrentUser(user);
    }, []);

    // After a successful purchase the button stays busy while the browser leaves for Stripe.
    // Back from Checkout can restore this page from the back/forward cache, so free it again.
    // (A pageshow listener doesn't keep the page out of that cache; unload listeners would.)
    useEffect(() => {
        const handlePageShow = (event) => {
            if (event.persisted) {
                purchasingRef.current = false;
                setPurchasing(false);
            }
        };
        window.addEventListener('pageshow', handlePageShow);
        return () => window.removeEventListener('pageshow', handlePageShow);
    }, []);

    useEffect(() => {
        if (currentUser) {
            walletService.listPayments().then(response => {
                setPayments(Array.isArray(response.data) ? response.data : []);
            }).catch(error => {
                console.error('Error fetching payment history', errorSummary(error));
                setHistError(true);
            }).finally(() => {
                setHistLoading(false);
            });
        }
    }, [currentUser]);


    const handlePurchase = async (amount) => {
        // One Checkout session per click: a second click while the first is pending is ignored.
        if (purchasingRef.current) return;
        purchasingRef.current = true;
        setPurchasing(true);
        setPurchaseError('');
        try {
            const data = await walletService.purchaseTokens(amount);
            if (!data || !data.url) {
                throw new Error('No checkout URL in the response');
            }
            // The button stays disabled while the browser leaves for Stripe.
            window.location.href = data.url;
        } catch (error) {
            console.error('Error during purchase:', errorSummary(error));
            // Only a request that never reached the server gets its own text; never the server's.
            setPurchaseError(error && error.response
                ? PURCHASE_ERROR_MESSAGE
                : getApiErrorMessage(error, PURCHASE_ERROR_MESSAGE));
            purchasingRef.current = false;
            setPurchasing(false);
        }
    };

    const sortedPayments = [...payments].sort((a, b) => {
        if (a[sortConfig.key] < b[sortConfig.key]) {
            return sortConfig.direction === 'ascending' ? -1 : 1;
        }
        if (a[sortConfig.key] > b[sortConfig.key]) {
            return sortConfig.direction === 'ascending' ? 1 : -1;
        }
        return 0;
    });

    const handleSort = (key) => {
        let direction = 'ascending';
        if (sortConfig.key === key && sortConfig.direction === 'ascending') {
            direction = 'descending';
        }
        setSortConfig({ key, direction });
    };

    const getSortIndicatorClass = (key) => {
        if (sortConfig.key === key) {
            return sortConfig.direction === 'ascending' ? 'ascending' : 'descending';
        }
        return '';
    };

    return (
        <div className='wallet-content'>
            <div className='header-content'>
                    <h2 className='pred-over'>Wallet</h2>
            </div>
            
            <p className='wallet-txt'>
                <svg style={{color: 'rgb(27, 69, 129)'}} xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="currentColor" className="bi bi-exclamation-circle-fill svg-wall" viewBox="0 0 16 16">
                    <path d="M16 8A8 8 0 1 1 0 8a8 8 0 0 1 16 0M8 4a.905.905 0 0 0-.9.995l.35 3.507a.552.552 0 0 0 1.1 0l.35-3.507A.905.905 0 0 0 8 4m.002 6a1 1 0 1 0 0 2 1 1 0 0 0 0-2"/>
                </svg>
                Making predictions with <strong>QLchemicals</strong> requires tokens. Once added, tokens are non-refundable and can only be used within the application.
            </p>
            <div className='up-wallet'>
                <div className='wallet-status'>
                    <div className='info-wallet'>
                    </div>
                    <div className='wallet'>
                        <p className='ws'>Wallet status</p>
                        {user ? (
                            <p className='token-number'>{`${user.coins} tokens`}</p>
                        ) : loading ? (
                            <p className='token-number'>Loading...</p>
                        ) : (
                            <p className='token-number balance-error' role='alert'>{BALANCE_ERROR_MESSAGE}</p>
                        )}
                    </div>
                </div>
                <div className='top-up'>
                    <h3 className='tu'>Top up your wallet</h3>
                    <div className='buy-options'>
                        <div 
                            className={`buy-opt ${selectedAmount === 1 ? 'active-buy-opt' : ''}`} 
                            onClick={() => setSelectedAmount(1)}
                        >
                            <div className='nr-token'>
                                1 token
                            </div>
                            <div className='tok-price'>9.99$</div>
                        </div>
                        <div 
                            className={`buy-opt ${selectedAmount === 10 ? 'active-buy-opt' : ''}`} 
                            onClick={() => setSelectedAmount(10)}
                        >
                            <div className='nr-token'>
                                10 tokens
                            </div>
                            <div className='tok-price'>89.99$</div>
                        </div>
                            <div 
                                className={`buy-opt ${selectedAmount === 100 ? 'active-buy-opt' : ''}`} 
                                onClick={() => setSelectedAmount(100)}
                            >
                            <div className='nr-token'>
                                100 tokens
                            </div>
                            <div className='tok-price'>849.99$</div>
                        </div>
                    </div>
                    <button 
                        className='purchase-btn' 
                        disabled={!selectedAmount || purchasing}
                        onClick={() => handlePurchase(selectedAmount)}
                    >
                        {purchasing ? 'Redirecting…' : 'Continue with this amount'}
                    </button>
                    {purchaseError && <p className='wallet-error' role='alert'>{purchaseError}</p>}

                    <div className='wall-notes'>
                        <div>
                            <FontAwesomeIcon className='env-wall' icon={faEnvelope} />
                        </div>
                        <div>
                            <p className='note-wallet'>Looking for an <strong>enterprise or academic</strong> license?</p>
                            <p className='note-wallet'>
                                Contact us via the <a href="#/contact" className="contact-link">form</a> to receive a personalized quote.
                            </p>
                        </div>
                    </div>
                </div>
            </div>
            <div className='pay-history'>
                {histLoading ? (
                    <div>
                        <h3 className='ph'>Payment history</h3>
                        <div className='loader-div'>
                            
                            <HashLoader className='loader' size={120} color='#0b5578' />
                        </div>
                    </div>
                ) : histError ? (
                    <div className='no-pay'>
                        <h3 className='ph'>Payment history</h3>
                        <p className='wallet-error' role='alert'>{HISTORY_ERROR_MESSAGE}</p>
                    </div>
                ) : sortedPayments.length === 0 ? (
                    <div className='no-pay'>
                        <h3 className='ph'>Payments history</h3>
                        <p className='no-payments'>No payments to display</p>
                    </div>
                ) : (
                    <div className='table-container'>
                        <h3 className='ph'>Payment history</h3>
                        <table className='payments-table'>
                            <thead>
                                <tr>
                                    <th onClick={() => handleSort('created')} className={getSortIndicatorClass('created')}>Purchase Date</th>
                                    <th onClick={() => handleSort('amount')} className={getSortIndicatorClass('amount')}>Price</th>
                                    <th onClick={() => handleSort('status')} className={getSortIndicatorClass('status')}>Status</th>
                                    <th className='download-invoice'>Invoice</th>
                                </tr>
                            </thead>
                            <tbody>
                                {sortedPayments.map((payment,idx) => (
                                    <tr className={`tr-color ${idx % 2 === 0 ? 'odd-row' : ''}`} key={payment.id}>
                                
                                        <td>{payment.created}</td>
                                        <td>{payment.amount} $</td>
                                        <td className={`td-stat ${payment.status.toLowerCase()}`}>{payment.status}</td>
                                        <td className='download-invoice'>
                                            {payment.invoice_url ? (
                                                <a 
                                                    href={payment.invoice_url} 
                                                    target="_blank" 
                                                    rel="noopener noreferrer" 
                                                >   
                                                    <FontAwesomeIcon className='down-inv-icon' icon={faFileInvoiceDollar} />
                                                </a> ) :
                                                (
                                                    <div></div>
                                                )
                                            }
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}
            </div>
            
        </div>


    );
};

export default WalletContent;
